/**
 * Money presentation — FRONTEND_DESIGN_SYSTEM.md §3.
 *
 * The backend sends money as a STRING of minor units with a separate ISO
 * currency, and never as a float. That is preserved here: the string is split
 * on the currency's own exponent rather than divided, so no amount passes
 * through a binary floating-point value on its way to the screen.
 *
 * Nothing in this module computes. There is no tax, no commission and no
 * platform fee, because OD-6 leaves all three undefined — a figure assembled
 * here would be an invention, not a total.
 */
/** The currency's minor-unit exponent, from Intl rather than a hand-kept table. */
export function currencyExponent(currency) {
    try {
        return (new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
            .maximumFractionDigits ?? 2);
    }
    catch {
        return 2;
    }
}
/**
 * `"1250000"` + `INR` → `"12,500.00"`. Returns the digits only; the currency
 * is rendered separately so a column of amounts aligns on the decimal.
 */
export function formatMinorUnits(minor, currency) {
    const exponent = currencyExponent(currency);
    const negative = minor.startsWith('-');
    const digits = (negative ? minor.slice(1) : minor).replace(/\D/g, '') || '0';
    const padded = digits.padStart(exponent + 1, '0');
    const whole = padded.slice(0, padded.length - exponent) || '0';
    const fraction = exponent === 0 ? '' : padded.slice(padded.length - exponent);
    const body = fraction === '' ? groupWhole(whole) : `${groupWhole(whole)}.${fraction}`;
    return negative ? `-${body}` : body;
}
/**
 * The whole part, grouped the way the VIEWER's locale groups digits.
 *
 * An Indian reader expects lakh/crore grouping and a European reader does not;
 * the currency does not decide that, the reader does. So this asks `Intl`
 * rather than hard-coding a separator.
 *
 * The engines disagree about what `format` will take, and the disagreement is
 * not academic: Hermes' `Intl` — the engine this product's phone app runs on —
 * throws `Cannot convert BigInt to number` outright, which took down the whole
 * equipment screen on a device while every browser rendered it perfectly.
 *
 * So: a `BigInt` where it is accepted, because it is exact at any size; a
 * `Number` where it is not and the value is inside the safe-integer range,
 * which produces the identical string; and the ungrouped digits above that.
 * The last case prints an unusual-looking figure, which is the right failure —
 * a grouped number that had been quietly rounded would look perfectly normal
 * and be wrong.
 */
function groupWhole(whole) {
    const formatter = new Intl.NumberFormat(undefined);
    try {
        return formatter.format(BigInt(whole));
    }
    catch {
        const value = Number(whole);
        return Number.isSafeInteger(value) ? formatter.format(value) : whole;
    }
}
/** The ISO code, shown beside the amount rather than as a glyph. */
export function currencyLabel(currency) {
    return currency.toUpperCase();
}
//# sourceMappingURL=money.js.map